/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { colors, borderRadius, shadows, typography, transitions } from '../../styles/theme';
import { ChevronDownIcon } from '../common';

const sectionStyles = css`
  background: ${colors.background.tertiary};
  padding: 3rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const containerStyles = css`
  max-width: 800px;
  margin: 0 auto;
`;

const sectionHeaderStyles = css`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const accordionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const itemStyles = css`
  background: white;
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
  overflow: hidden;
  transition: box-shadow ${transitions.fast};

  &:hover {
    box-shadow: ${shadows.md};
  }
`;

const headerStyles = css`
  margin: 0;

  button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.5rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.base};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
    transition: background ${transitions.fast};

    &:hover {
      background: ${colors.neutral[50]};
    }
  }

  .chevron {
    width: 20px;
    height: 20px;
    color: ${colors.neutral[400]};
    transition: transform ${transitions.fast};
    flex-shrink: 0;
  }

  .chevron.open {
    transform: rotate(180deg);
    color: ${colors.primary[500]};
  }
`;

const contentStyles = css`
  padding: 0 1.5rem 1.25rem;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  line-height: 1.7;
`;

const faqData = [
  {
    question: 'Çekici hizmeti ne kadar sürede gelir?',
    answer: 'Bulunduğunuz konuma bağlı olarak ortalama 15-30 dakika içinde en yakın sürücümüz size ulaşır. Yoğun saatlerde bu süre biraz uzayabilir.',
  },
  {
    question: 'Hizmet ücretleri nasıl belirleniyor?',
    answer: 'Ücretler mesafe, hizmet türü ve araç tipine göre hesaplanır. Talep oluşturduğunuzda tahmini ücret bilgisi gösterilir, sürpriz ücret yoktur.',
  },
  {
    question: 'Hangi illerde hizmet veriyorsunuz?',
    answer: 'Türkiye\'nin 81 ilinde geniş sürücü ağımız ile hizmet vermekteyiz. Kırsal bölgelerde de sürücülerimize ulaşabilirsiniz.',
  },
  {
    question: 'Ödeme yöntemleri nelerdir?',
    answer: 'Kredi kartı, banka kartı ve nakit ödeme seçenekleri mevcuttur. Uygulama üzerinden güvenli online ödeme yapabilirsiniz.',
  },
  {
    question: 'Hizmeti iptal edebilir miyim?',
    answer: 'Sürücü yola çıkmadan önce ücretsiz iptal yapabilirsiniz. Sürücü yola çıktıktan sonra iptal koşulları için uygulama içi bilgilendirmeye bakabilirsiniz.',
  },
  {
    question: 'Sürücüler güvenilir mi?',
    answer: 'Tüm sürücülerimiz kimlik doğrulama, ehliyet kontrolü ve geçmiş taramasından geçer. Ayrıca kullanıcı değerlendirme sistemi ile hizmet kalitesi sürekli izlenmektedir.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section css={sectionStyles}>
      <div css={containerStyles}>
        <div css={sectionHeaderStyles}>
          <h2>Sıkça Sorulan Sorular</h2>
          <p>Merak ettiğiniz soruların yanıtları</p>
        </div>
        <div css={accordionStyles}>
          {faqData.map((faq, index) => (
            <div key={index} css={itemStyles}>
              <h3 css={headerStyles}>
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  {faq.question}
                  <ChevronDownIcon className={`chevron ${openIndex === index ? 'open' : ''}`} />
                </button>
              </h3>
              {openIndex === index && (
                <div css={contentStyles} id={`faq-answer-${index}`}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
